import { Sparkles } from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  type FC,
  type SyntheticEvent
} from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { caseTreeQueryKey } from "@/features/caseFiles/hooks/useCaseFilesystem";
import { nodeTitle } from "@/features/caseFiles/nodeNames";
import type { FileCardStatus, FsNode } from "@/features/caseFiles/types";
import { queryClient } from "@/lib/query/queryClient";
import { cn } from "@/lib/utils";

import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseRoute } from "../useCaseRoute";
import {
  fileCardQueryKey,
  isCardBuilding,
  unavailableMessage,
  useFileCard,
  useRequestFileCard
} from "./fileCard";
import { cardTabTitle } from "./fileCardSummary";

const stop = (e: SyntheticEvent) => e.stopPropagation();

type Props = {
  node: FsNode;
  /** The row is selected, focused or has its menu open: show without hover. */
  revealed?: boolean;
};

/**
 * The sparkle beside a case file. Hover says Summaries. A file that already
 * has a card opens it as a workspace tab; otherwise this starts a build and
 * spins until the card lands. Closing that tab leaves the card where it is.
 */
export const FileCardButton: FC<Props> = memo(function FileCardButton({
  node,
  revealed = false
}) {
  const { individualId } = useCaseRoute();
  const { open, activeArtifact } = useCaseIde();
  const cached = queryClient.getQueryData<{ status: FileCardStatus }>(
    fileCardQueryKey(individualId, node.id)
  )?.status;
  const { data: card } = useFileCard(
    individualId,
    node.id,
    isCardBuilding(cached)
  );
  const request = useRequestFileCard(individualId, node.id);
  const title = nodeTitle(node);
  const status = card?.status;
  const building = request.isPending || isCardBuilding(status);
  const hasCard = status ? status === "ready" : Boolean(node.has_card);
  const viewing =
    activeArtifact?.kind === "card" && activeArtifact.id === node.id;

  const openCard = useCallback(
    () => open({ kind: "card", id: node.id, title: cardTabTitle(title) }),
    [open, node.id, title]
  );

  const seen = useRef<FileCardStatus | undefined>(status);
  useEffect(() => {
    const was = seen.current;
    seen.current = status;
    if (!isCardBuilding(was) || isCardBuilding(status)) return;
    if (status === "ready") {
      void queryClient.invalidateQueries({
        queryKey: caseTreeQueryKey(individualId)
      });
      toast.success(`Card ready for ${title}.`, {
        action: { label: "View", onClick: openCard }
      });
    } else if (status === "failed") {
      toast.error(`Couldn't build the card for ${title}.`);
    }
  }, [status, individualId, title, openCard]);

  if (!individualId) return null;

  const onClick = () => {
    if (building) return;
    if (hasCard) {
      openCard();
      return;
    }
    request.mutate(undefined, {
      onSuccess: (next) => {
        if (next.status === "ready") openCard();
        else if (next.status === "unavailable")
          toast.info(unavailableMessage(next));
      },
      onError: () => toast.error(`Couldn't start the card for ${title}.`)
    });
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      tabIndex={-1}
      title="Summaries"
      aria-label={`Summaries: ${title}`}
      aria-busy={building || undefined}
      data-testid="file-row-card"
      data-has-card={hasCard ? "true" : "false"}
      data-pending={building ? "true" : undefined}
      onPointerDown={stop}
      onMouseDown={stop}
      onDoubleClick={stop}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "button-shimmer-outline ml-1 size-5 shrink-0 rounded-md text-fg-4 hover:bg-transparent hover:text-fg-3",
        "hidden group-hover:inline-flex focus-visible:inline-flex",
        (revealed || building || viewing) && "inline-flex"
      )}
    >
      {building ? (
        <Spinner className="size-3" />
      ) : (
        <Sparkles className="size-3" aria-hidden />
      )}
    </Button>
  );
});
